import React, { useState, useRef } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

interface HappySnapSubmissionProps {
  eventId: string;
  onBack: () => void;
}

const HappySnapSubmission: React.FC<HappySnapSubmissionProps> = ({ eventId, onBack }) => {
  const { userId } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!photo || !userId) return;
    setSubmitting(true);
    setError(null);
    try {
      const imageCompression = (await import('browser-image-compression')).default;
      const compressed = await imageCompression(photo, { maxSizeMB: 2, maxWidthOrHeight: 2048, useWebWorker: true });
      const contentType = compressed.type || photo.type || 'image/jpeg';

      const result = await (client.mutations as any).submitHappySnap({ eventId, contentType, caption: caption.trim() || undefined, memberName: '' });
      const data = parseLambdaResponse(result?.data);
      if (!data?.success) throw new Error(data?.error || 'Submission failed');

      await fetch(data.uploadUrl, { method: 'PUT', body: compressed, headers: { 'Content-Type': contentType } });
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit photo');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">📸</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Photo Submitted!</h2>
          <p className="text-gray-600 mb-6">Your happy snap is pending review by an admin.</p>
          <div className="space-y-3">
            <button onClick={() => { setSubmitted(false); setPhoto(null); setPreview(null); setCaption(''); if (fileRef.current) fileRef.current.value = ''; }} className="w-full px-6 py-3 bg-fishing-blue text-white rounded-lg font-medium hover:bg-blue-700">Submit Another</button>
            <button onClick={onBack} className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">Back to Competitions</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900">📸 Happy Snaps</h1>
            <button onClick={onBack} className="text-gray-500 hover:text-gray-700" aria-label="Back">✕</button>
          </div>
          <p className="text-gray-600 text-sm">Share a fun photo from competition day!</p>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-800">{error}</div>}

        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          {preview ? (
            <div className="relative mb-3">
              <img src={preview} alt="Preview" className="w-full rounded-lg object-cover max-h-64" />
              <button type="button" onClick={() => { setPhoto(null); setPreview(null); if (fileRef.current) fileRef.current.value = ''; }} className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 shadow-lg" aria-label="Remove">✕</button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full py-8 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50 hover:bg-blue-100 flex flex-col items-center">
              <span className="text-4xl mb-2">📷</span>
              <span className="text-fishing-blue font-medium">Take Photo or Upload</span>
            </button>
          )}
          {/* No `capture` — same reasoning as the catch forms: it hid the photo
              library, and a happy snap is very often a shot taken earlier in the
              day rather than one being taken right now. */}
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <label htmlFor="caption" className="block text-sm font-semibold text-gray-900 mb-2">Caption (optional)</label>
          <input id="caption" type="text" maxLength={200} value={caption} onChange={(e: any) => setCaption(e.target.value)} placeholder="What's happening in this photo?" className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue" />
          <div className="text-xs text-gray-400 text-right mt-1">{caption.length}/200</div>
        </div>

        <button onClick={handleSubmit} disabled={!photo || submitting} className="w-full py-4 bg-fishing-blue text-white rounded-lg text-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-lg">
          {submitting ? 'Submitting…' : 'Submit Happy Snap'}
        </button>
      </div>
    </div>
  );
};

export default HappySnapSubmission;
